import { EventInteraction, useTracking } from '@eventbrite/ads';
import { Carousel } from '@eventbrite/eds-carousel';
import {
    CONSUMER_CARD_DEFAULTS,
    EventCard,
    FormattedEvent,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import React, { useContext, useRef } from 'react';
import { STATSIG_EVENT_NAMES } from '../../constants';
import { TrackingContext } from '../../context';
import '../../styles/related-events.scss';
import {
    label,
    shareType,
    trackShareAction,
} from '../../tracking/trackShareAction';
import { AffiliateCodes, GACodes } from '../../types';
import { ShareProps } from '../ShareEvent/ShareEventRoot';
import { getSortedEvents } from '../_shared/getSortedEvents';
import { RelatedEventsChildrenProps } from './RelatedEvents';

const CAROUSEL_HEADING = gettext('Other events you may like');

const getEventCardMap = ({
    events,
    promotedEvents,
    isAuthenticated,
    shareComponentProps,
    isSalesEnded,
}: {
    events: FormattedEvent[] | undefined;
    promotedEvents: FormattedEvent[];
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    isSalesEnded: boolean;
}): JSX.Element[] => {
    if (!events) {
        return [];
    }

    const eventsToRender = getSortedEvents(events as any, promotedEvents);

    return eventsToRender.map((event: FormattedEvent) => {
        const shareEventProps = {
            eventId: event.id,
            eventName: event.name,
            eventUrl: event.url,
            ...shareComponentProps,
        };

        const eventKey = event.promotedListingMetadata?.adId || event.id;

        return (
            <EventCard
                {...CONSUMER_CARD_DEFAULTS}
                heapLocationString={
                    event.isPromoted
                        ? 'Related Events - Event Carousel - Promoted'
                        : undefined
                }
                affCode={AffiliateCodes.AFF_RELATED_EVENTS_LIVE}
                key={eventKey}
                eventData={event}
                gaCategory="related-events"
                isAuthenticated={isAuthenticated}
                shareOptions={shareEventProps}
                onDidShareClick={(id: shareType) =>
                    trackShareAction(label(id), GACodes.GA_CATEGORY_EVENT_CARD)
                }
                onWillShareClick={() => {
                    trackShareAction(
                        GACodes.GA_LABEL_OPEN_MODAL,
                        GACodes.GA_CATEGORY_EVENT_CARD,
                    );
                    isSalesEnded &&
                        logEvent(
                            STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_SHARE,
                        );
                }}
                onClick={() =>
                    isSalesEnded &&
                    logEvent(STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_CLICK)
                }
                onSave={(saved) => {
                    isSalesEnded &&
                        // bug exists where saved doesn't update before it is passed, so checking for opposite of true
                        !saved &&
                        logEvent(
                            STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_LIKE,
                        );
                }}
            />
        );
    });
};

function getEventFromId(
    props: RelatedEventsChildrenProps,
    eventIdToFind: string,
    isPromoted: boolean,
) {
    const promotedEvents = props.promotedEvents || [];
    const organicEvents = props.events || [];
    if (isPromoted) {
        const eventPosition = promotedEvents.findIndex(
            (event) => event?.id === eventIdToFind,
        );
        return {
            event: promotedEvents[eventPosition],
            absolutePosition: eventPosition + 1,
        };
    }
    const eventPosition = organicEvents.findIndex(
        (event) => event?.id === eventIdToFind,
    );
    return {
        event: organicEvents[eventPosition],
        absolutePosition: eventPosition + 1,
    };
}

const DESKTOP_SLIDE_SIZE = 3;
const MOBILE_SLIDE_SIZE = 1;

const OtherEventsYouMayLike = (props: RelatedEventsChildrenProps) => {
    const currentPage = useRef({ number: 0, size: 0 });
    const context = useContext(TrackingContext);

    const { ref } = useTracking<HTMLDivElement>(({ id, event }) =>
        aggregateContext({
            id,
            event,
            context,
            page: currentPage.current,
            props,
        }),
    );

    const eventCardMap = getEventCardMap({
        events: props.events,
        promotedEvents: props.promotedEvents || [],
        isAuthenticated: props.isAuthenticated,
        shareComponentProps: props.shareComponentProps,
        isSalesEnded: props.isSalesEnded,
    });

    const wrapEventCardMapForDesktop = () => {
        const slides = [];

        for (let i = 0; i < eventCardMap.length; i += DESKTOP_SLIDE_SIZE) {
            const slideItem = eventCardMap.filter(
                (_, index) => index >= i && index < i + DESKTOP_SLIDE_SIZE,
            );
            slides.push(slideItem);
        }
        return slides;
    };

    const eventCardMapForDesktop = wrapEventCardMapForDesktop().map(
        (slide, index) => {
            return (
                <div className="desktop-slide" key={index}>
                    {slide}
                </div>
            );
        },
    );
    if (!props.events || props.events.length <= 0) {
        return null;
    }
    return (
        <section
            ref={ref}
            className="related-events g-grid g-grid--page-margin-manual"
        >
            <h2 className="eds-is-hidden-accessible">{CAROUSEL_HEADING}</h2>
            <div className="eds-g-cell eds-g-cell--has-overflow eds-g-cell-1-1 eds-l-pad-hor-5 eds-l-ln-pad-hor-0 eds-l-lg-pad-hor-0 eds-l-lw-pad-hor-0 eds-l-mar-bot-16">
                <div className="eds-show-up-mn" data-testid="carousel-desktop">
                    <Carousel
                        onTransition={(toPage: number) => {
                            currentPage.current = {
                                number: toPage,
                                size: DESKTOP_SLIDE_SIZE,
                            };
                        }}
                        hasTopControls={true}
                        isNotContinuous={true}
                        alwaysShowSideNavigation={true}
                        usesNewStyle={true}
                        title={CAROUSEL_HEADING}
                        fullWidth={true}
                    >
                        {eventCardMapForDesktop}
                    </Carousel>
                </div>

                <div
                    className="eds-show-down-sw carousel-mobile"
                    data-testid="carousel-mobile"
                >
                    <Carousel
                        onTransition={(toPage: number) => {
                            currentPage.current = {
                                number: toPage,
                                size: MOBILE_SLIDE_SIZE,
                            };
                        }}
                        alwaysShowSideNavigation={true}
                        hasTopControls={true}
                        isNotContinuous={true}
                        usesNewStyle={true}
                        title={CAROUSEL_HEADING}
                        fullWidth={true}
                    >
                        {eventCardMap}
                    </Carousel>
                </div>
            </div>
        </section>
    );
};

const RELATED_EVENTS_PLACEMENT_NAME = 'related_events';
const LIVE_EVENTS_PLACEMENT_NAME = 'events_carousel';

function aggregateContext({
    id,
    context,
    event: eventData,
    page,
    props,
}: Omit<EventInteraction, 'action'> & {
    context: any;
    page: { number: number; size: number };
    props: any;
}) {
    const { event, absolutePosition } = getEventFromId(
        props,
        id,
        eventData.isPromoted,
    );
    return {
        user: {
            id: context.userId,
            locale: context.locale,
            guestId: context.guestId,
            sessionId: context.sessionId,
        },
        adId: event?.promotedListingMetadata?.adId,
        place: {
            name: RELATED_EVENTS_PLACEMENT_NAME,
            position:
                absolutePosition -
                page.number * page.size +
                (page.number > 0 ? props.promotedEvents.length : 0),
            page: page.number + 1,
            context: {
                related_event_id: context.event?.id,
                is_online: context.event?.isOnline,
                place_id: context.event?.localityPlaceId,
            },
            tabKey: '',
            subInterface: {
                name: LIVE_EVENTS_PLACEMENT_NAME,
                position: '',
            },
            placementId: event?.promotedListingMetadata?.placementId,
        },
        venueId: event?.venue?.id,
    };
}

export default OtherEventsYouMayLike;
